import { Component, Input, OnInit, ViewChild } from '@angular/core';
import { AbstractControl, FormBuilder, FormControl, FormGroup, ValidatorFn, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { CallService } from 'src/app/services/call.service';
import { DoctorService } from 'src/app/services/doctor.service';
import { NotificationsService } from 'src/app/services/notifications.service';
import { PatientService } from 'src/app/services/patient.service';
import { Call } from 'src/app/models/call.model';
import { CallStatus } from 'src/app/enums/call-status.enum';
import { AppPaths } from 'src/app/enums/app-paths.enum';
import { ToastService } from 'src/app/services/toast.service';
import { Patient } from 'src/app/models/patient.model';
import * as _ from 'lodash';
import { pairwise } from 'rxjs/operators';
import { Task } from '../../models/task.model';
import moment from 'moment';
import { MatDialog } from '@angular/material/dialog';
import { PrescriptionAttachment, UploadExternalPrescriptionDialogComponent } from '../upload-external-prescription-dialog/upload-external-prescription-dialog.component';
import { PrescriptionStatus } from 'src/app/enums/prescription-status.enum';
import { AttachmentService } from 'src/app/services/attachment.service';
import { RatingService } from 'src/app/services/rating.service';
import { ForeignPrescriptionStatus } from 'src/app/enums/foreign-prescription-status.enum';
import { MONITORING_TAG_BY_CIE10 } from 'src/app/consts/monitoring-tags-cie10.const';
import { PharmacyPickupComponent } from './pharmacy-pickup/pharmacy-pickup.component';
import { PrescriptionManagers } from 'src/app/enums/prescription-managers.enum';
import { MonitoringTags } from 'src/app/enums/monitoring-tags.enum';
import { COUNTRY_CODE } from 'src/app/consts/country-code.const';

@Component({
  selector: 'app-rate',
  templateUrl: './rate.component.html',
  styleUrls: ['./rate.component.css'],
  standalone: false
})
export class RateComponent implements OnInit {
  @Input('call')
  call!: Call;

  readonly CallStatus = CallStatus;

  @ViewChild(PharmacyPickupComponent) pharmacyComponent?: PharmacyPickupComponent;
  displayPharmacy = false;
  submitting = false;
  loading = true;

  starCount = 5;
  formGroup!: FormGroup;
  monitoringFormGroup!: FormGroup;
  isDiagnosisSupportedForMonitoring = false;
  disableOtherDoctorMonitoring = false;
  disableMonitoringForm: boolean | undefined = false;
  disableMonitoringButton = false;
  hasActiveTask = false;
  hasSupportedMonitorings = false;
  patient!: Patient;
  canPrescribeMedication!: boolean;
  timeRegExp!: RegExp;
  patientActiveTasks!: Task[];
  displayRequiresPrescription = false;
  canUploadExternalPrescription = false;
  hasProviderCountryEnrollment = false;

  constructor(
    private router: Router,
    private matDialog: MatDialog,
    private callService: CallService,
    private patientService: PatientService,
    private notificationsService: NotificationsService,
    private doctorService: DoctorService,
    private formBuilder: FormBuilder,
    private toastService: ToastService,
    private attachmentService: AttachmentService<PrescriptionAttachment>,
    private ratingService: RatingService
  ) { }

  async ngOnInit(): Promise<void> {
    this.setTimeRegExp();
    this.isDiagnosisSupportedForMonitoring = this.checkIsDiagnosisSupportedForMonitoring();
    this.buildForms();
    this.subscribeToFormChanges();

    if([CallStatus.ENDED_MANUAL, CallStatus.ENDED].includes(this.call.status as CallStatus) && this.call.provider?.requiresPrescriptionForAnotherCountry) {
      this.displayRequiresPrescription = true;
    }

    [this.canPrescribeMedication, this.patientActiveTasks, this.hasProviderCountryEnrollment] = await Promise.all([
      this.doctorService.getMedicalDocumentCapabilities(this.call.id).then(capabilities => capabilities.canPrescribeMedication),
      this.patientService.getActivePatientTasks(this.call.patient.id),
      this.doctorService.hasEnrollmentInProviderCountry(this.call.provider.id)
    ]);

    this.displayPharmacy = this.shouldDisplayPharmacy();
    if (this.displayPharmacy) {
      this.doctorDuration.setValidators(control => control.value ? this.timeValidator()(control) : null);
      this.doctorDuration.updateValueAndValidity();
    }

    this.loading = false;
    this.checkDisableRequiresPrescriptionControl();

    this.patient = this.call.patient;

    const { supportedMonitorings } = this.call.provider;
    this.hasSupportedMonitorings = (supportedMonitorings && supportedMonitorings?.length > 0) || false;

    this.hasActiveTask = !!this.patient.onMonitoring && this.patientActiveTasks.length > 0;
    this.disableOtherDoctorMonitoring = !!(this.patient.onMonitoring || this.patient.needsMonitoring || this.patient.needsValidation) // disable if patient is already on monitoring or needs monitoring.
    this.disableMonitoringForm =
      !this.call.doctor.canMonitoring || // disable if doctor is not allowed to start monitoring
      this.patient.needsMonitoring || // disable if patient needs monitoring
      this.patient.needsValidation || // disable if patient needs validation
      this.hasActiveTask || // disable if the patient is already on monitoring with the current doctor and the current active task is not complete
      (
        this.patient.onMonitoring &&
        !!this.patient.doctorAssigned &&
        this.patient.doctorAssigned != this.call.doctor.id
      ) // disable if the patient is already monitoring but the doctor is no the doctor assigned

    this.canUploadExternalPrescription = moment().diff(moment(this.call.createdAt), 'hours') < 36;
  }

  private shouldDisplayPharmacy(): boolean {
    const { manual, country, originalCall } = this.call;
    if (!originalCall) return false;
    const isUnitedStates = country?.countryCode === COUNTRY_CODE.UNITED_STATES;
    const isInternalManagement = originalCall.prescriptionManager === PrescriptionManagers.INTERNAL_MANAGEMENT;
    const isPendingPharmacy = [ForeignPrescriptionStatus.ASSIGNED, ForeignPrescriptionStatus.WAITING_FOR_PHARMACY].includes(
      originalCall.foreignPrescriptionStatus!
    );

    const pharmacy = originalCall.foreignPrescriptionData;
    const hasRegisteredPharmacy = !!(pharmacy?.pharmacyName || pharmacy?.pharmacyAddress);

    return !!(manual && isUnitedStates && isInternalManagement && (isPendingPharmacy || hasRegisteredPharmacy) && this.hasProviderCountryEnrollment);
  }

  private checkIsDiagnosisSupportedForMonitoring(): boolean {
    const diagnosisCode = this.call.diagnosis?.diagnosis;
    const supported = this.call.provider.supportedMonitorings || [];
    if (!supported.length) return false;

    const hasMappedCie10Tag = diagnosisCode && MONITORING_TAG_BY_CIE10[diagnosisCode] !== undefined;
    if (hasMappedCie10Tag) {
      const monitoringTag = MONITORING_TAG_BY_CIE10[diagnosisCode];
      return supported.includes(monitoringTag);
    }
    return supported.includes(MonitoringTags.CHRONIC);
  }

  setTimeRegExp(): void {
    // Force MM:ss format. Also allows three digits for minutes, ex. 120:53
    if(
      this.call.diagnosis?.connectionFailed ||
      this.call.diagnosis?.couldNotContact ||
      this.call.diagnosis?.wrongPhone ||
      this.call.diagnosis?.consultationPreviouslySolved
    ) {
      this.timeRegExp = /^(\d{1,3}):(\d{2})$/;
    } else {
      this.timeRegExp = /^(?!00:00)(\d{1,3}):(\d{2})$/; // '00:00' not allowed
    }
  }

  buildForms(): void {
    let duration = '';
    if(this.call.doctorDuration) {
      const minutes = Math.floor(this.call.doctorDuration / 60).toLocaleString('en-US', {
        minimumIntegerDigits: 2,
        useGrouping: false
      });

      const seconds = (this.call.doctorDuration - Number(minutes) * 60).toLocaleString('en-US', {
        minimumIntegerDigits: 2,
      });

      duration = `${minutes}:${seconds}`;
    }

    this.formGroup = this.formBuilder.group({
      doctorStars: [this.call.doctorStars || 0, [Validators.required, Validators.min(1)]],
      doctorText: { value: this.call.doctorText || '', disabled: this.call.doctorText || this.call.status === CallStatus.ENDED_MANUAL },
      doctorDuration: [ {value: duration, disabled: this.call.doctorDuration}, [Validators.required, this.timeValidator()]],
      requiresPrescription: { value: this.call.requiresPrescription, disabled: true },
    });

    this.monitoringFormGroup = this.formBuilder.group({
      otherDoctor: { value: null, disabled: !this.isDiagnosisSupportedForMonitoring },
    });
  }

  checkDisableRequiresPrescriptionControl(): void {
    const canRemoveRequiresPrescriptionForAnotherCountry =
      this.call.provider.requiresPrescriptionForAnotherCountry &&
      this.call.prescriptionManager &&
      this.call.foreignPrescriptionStatus === ForeignPrescriptionStatus.PENDING_ASSIGNMENT;

    const disableRequiresPrescriptionForAnotherCountry =
      (this.canPrescribeMedication && !this.call.provider.isRedGlobal) || (this.call.requiresPrescription && !canRemoveRequiresPrescriptionForAnotherCountry);

    disableRequiresPrescriptionForAnotherCountry ? this.requiresPrescription.disable() : this.requiresPrescription.enable();
  }

  timeValidator(): ValidatorFn {
    return (control: AbstractControl<string>): { [key: string]: boolean } | null => {
      if (this.timeRegExp.test(control.value) && Number(control.value.split(":")[1]) < 60) {
        return null;
      }
      return { invalidTimeFormat: true };
    }
  }

  subscribeToFormChanges(): void {
    // Insert ':' after minutes
    this.doctorDuration.valueChanges.pipe(pairwise()).subscribe(([previousValue, value]) => {
      const splittedPreviousValue = previousValue.split('');
      const splittedValue = value.split('');

      if(
        splittedValue.length === 1 ||
        splittedValue[splittedValue.length - 1] === ':' ||
        splittedPreviousValue[splittedPreviousValue.length - 1] === ':'
      ) {
        return;
      }

      const number = parseInt(splittedValue[1]);
      if(splittedValue.length === 2 && _.isInteger(number)) {
        this.doctorDuration.patchValue(value + ':');
      }
    })
  }

  async submit(): Promise<void> {
    if (this.submitting || this.loading || this.formGroup.invalid) return;
    this.submitting = true;
    if (this.pharmacyComponent && !await this.pharmacyComponent.confirmBeforeFinishing()) {
      this.submitting = false;
      return;
    }

    const duration = this.transformDuration();
    let updateBody = {};

    if(this.call.status === CallStatus.ENDED_MANUAL) {
      updateBody = {
        requiresPrescription: this.requiresPrescription.value,
      }
    } else {
      updateBody = {
        doctorStars: this.doctorStars.value,
        doctorText: this.doctorText.value,
        ...(duration !== undefined && { doctorDuration: duration }),
        status: CallStatus.ENDED_MANUAL,
        ...(this.call.status === CallStatus.ENDED && { requiresPrescription: this.requiresPrescription.value })
      }
    }

    const pharmacy = this.pharmacyComponent?.getCompletedPharmacy();
    if (pharmacy) updateBody = { ...updateBody, foreignPrescriptionPharmacy: pharmacy };

    try {
      await Promise.all([
        this.notifyToSendFilesByWhatsApp(),
        this.callService.update(this.call.id, updateBody)
      ]);
      this.ratingService.registerMedicalCareRecord(this.call.id);
      this.router.navigate([AppPaths.MESSAGE], {state: { message: "successful_end_call" }});
    } catch (err) {
      const message = err.status === 401 ? err?.error?.message : null;
      const data = {
        message: message || "rate_error",
        error: err.status !== 401 ? err.error || err : null,
        title: "default_warning_message",
        actions: [{
          function: () => undefined,
          message: "default_accept"
        }]
      }
      this.notificationsService.openErrorDialog(data, false);
    } finally {
      this.submitting = false;
    }
  }

  transformDuration(): number | undefined {
    if (this.displayPharmacy && !this.doctorDuration.value) return 180;
    const splittedDuration = this.doctorDuration.value?.split(":");
    const minutes = Number(splittedDuration[0]);
    const seconds = Number(splittedDuration[1]);
    return minutes * 60 + seconds;
  }

  notifyToSendFilesByWhatsApp(): Promise<any> {
    const notify = {
      sendPrescriptions: this.call.provider.sendPrescriptionByWhatsApp,
      sendOrders: this.call.provider.sendOrderByWhatsApp,
    }
    return this.callService.notifyToSendFilesByWhatsApp(this.call.id, notify);
  }

  successMonitoring(): void {
    this.disableMonitoringButton = true;
    this.otherDoctor.disable();
    this.toastService.openSuccessToast("patient_monitoring_created");
  }

  updatePatientMonitoring(){
    this.patientService.setPatientNeedsMonitoring(this.call.patient.id, this.call.diagnosis?.diagnosis as string).then(() => {
      this.successMonitoring();
    }).catch(err =>{
      const error = err.error || err;
      this.notificationsService.openErrorDialog({
        message: "monitoring_error",
        error: error,
        actions: [
          {
            function: () => undefined,
            message: 'close'
          }
        ],
      }, false)
    })
  }

  onRateClick($event: MouseEvent, rating: number): void {
    $event.preventDefault();
    this.doctorStars.setValue(rating);
  }

  get isLastIteration(): boolean {
    return !!(this.patient.onMonitoring && !this.patientActiveTasks.length
      && (this.call.diagnosis?.connectionFailed || this.call.diagnosis?.couldNotContact || this.call.diagnosis?.wrongPhone));
  }

  openUploadExternalPrescriptionDialog(): void {
    let attachments = this.attachmentService.attachments;

    if (!attachments || attachments.length === 0) {
      attachments = this.call.prescriptions.filter(
        (prescription) => prescription.status !== PrescriptionStatus.DELETED_EXTERNAL_BY_DOCTOR
      ) as PrescriptionAttachment[];
    }

    this.matDialog.open(UploadExternalPrescriptionDialogComponent, {
      width: '600px',
      maxWidth: '90vw',
     
      data: {
        callId: this.call.id,
        prescriptions: attachments
      }
    });
  }

  // formGroup
  get doctorStars(): FormControl<number> { return this.formGroup.get('doctorStars') as FormControl }
  get doctorText(): FormControl<string> { return this.formGroup.get('doctorText') as FormControl }
  get doctorDuration(): FormControl<string> { return this.formGroup.get('doctorDuration') as FormControl }
  get requiresPrescription(): FormControl<boolean> { return this.formGroup.get('requiresPrescription') as FormControl }

  // monitoringFormGroup
  get otherDoctor(): FormControl<boolean> { return this.monitoringFormGroup.get('otherDoctor') as FormControl }
}
